import type { Schema } from "@/shared/components/Form/types";
import type { EditSchemaDefinition } from "@/shared/components/CRUD/types";
import { OrganizationsAutocompleteQuery } from "@/admin/entities/organizations";
import {
  ContractPaymentShowQuery,
  UpdateContractPaymentNoteMutation,
} from "@/admin/entities/contractPayments";
import type { ContractPaymentShowQueryType } from "@/types/graphql/admin.generated";
import { ContractPaymentCard } from "../domains/ContractPaymentCard";
import {
  CONTRACT_PAYMENT_CARD_TAB,
  contractPaymentTitle,
} from "../domains/contractPaymentCardHelpers";
import { CONTRACT_PAYMENT_STATUS_LABEL } from "../domains/billingLabels";

/** Чип «Просроченные» среди статусов: это флаг expired, а не статус. */
export const OVERDUE_STATUS_FILTER = "overdue";

/**
 * Фильтры раздела «Биллинг». Статус и «Просроченные» чипами (priority: "quick"),
 * остальное в панели сеткой по 4 в ряд, переключатель просрочки последним.
 */
export const filterSchema: Schema = {
  status: {
    label: "Статус",
    type: "select",
    priority: "quick",
    xs: 12,
    options: [
      { label: "Все", value: "" },
      ...Object.entries(CONTRACT_PAYMENT_STATUS_LABEL).map(
        ([value, label]) => ({ label, value })
      ),
      { label: "Просроченные", value: OVERDUE_STATUS_FILTER },
    ],
  },
  organizationId: {
    label: "Организация",
    type: "autocomplete",
    query: OrganizationsAutocompleteQuery,
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  invoiceDateFrom: {
    label: "Дата инвойса от",
    type: "date",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  invoiceDateTo: {
    label: "Дата инвойса до",
    type: "date",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
  },
  // Фильтр по наличию инвойса поддержан бэкендом (спека §9), но в интерфейсе
  // его не было (финальное ревью L1). Tri-state select, а не switch: у
  // "нет счёта" есть смысл как самостоятельное значение, а не просто
  // выключенный переключатель "есть счёт".
  hasInvoice: {
    label: "Наличие счёта",
    type: "select",
    xs: 12,
    md: 3,
    fullWidth: true,
    priority: "secondary",
    options: [
      { label: "Все", value: "" },
      { label: "Со счётом", value: "true" },
      { label: "Без счёта", value: "false" },
    ],
  },
  expired: {
    label: "Только просроченные",
    type: "switch",
    xs: 12,
    priority: "secondary",
  },
};

// hasInvoice это Boolean-путь ContractPaymentsFilter, а select всегда
// отдаёт строку: тот же приём, что у refunded/disputed в фильтре покупок
// (productPurchases/model/schemas.tsx), строки "true"/"false" приводятся
// к boolean здесь, а не полагаются на автоконверсию.
const BOOLEAN_FILTER_KEYS = new Set(["hasInvoice"]);

/**
 * Пустые значения в фильтр не уходят: пустая строка статуса означает «все»,
 * а выключенный переключатель просрочки это не фильтр.
 */
export function normalizeFilter(
  filter: Record<string, unknown>
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === "" || value === null || value === undefined) continue;
    // Чип «Просроченные» живёт в поле статуса, но в запрос уходит флагом.
    if (key === "status" && value === OVERDUE_STATUS_FILTER) {
      normalized.expired = true;
      continue;
    }
    if (key === "expired" && value !== true) continue;
    if (BOOLEAN_FILTER_KEYS.has(key) && typeof value === "string") {
      normalized[key] = value === "true";
      continue;
    }
    normalized[key] = value;
  }
  return normalized;
}

/**
 * Диалог платежа (спека 2026-10-16 §4.1): два плоских корневых шага,
 * карточка первой (открывается по ?tab=payment&id=), заметка формой.
 */
export const editSchema: EditSchemaDefinition = {
  [CONTRACT_PAYMENT_CARD_TAB]: {
    label: "Платёж",
    type: "show" as const,
    showQuery: ContractPaymentShowQuery,
    showComponent: ContractPaymentCard,
    getTitle: (data: unknown) => {
      const payment = (data as ContractPaymentShowQueryType | undefined)
        ?.contractPayment;
      return payment ? contractPaymentTitle(payment) : null;
    },
  },
  note: {
    label: "Заметка",
    type: "form" as const,
    editQuery: ContractPaymentShowQuery,
    updateMutation: UpdateContractPaymentNoteMutation,
    getEditData: (data: unknown) => ({
      note: (data as ContractPaymentShowQueryType).contractPayment.note ?? "",
    }),
    schema: {
      note: {
        label: "Заметка",
        type: "markdown",
        xs: 12,
      },
    },
  },
};
